"use client";

import {
  Cog6ToothIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Switch } from "@/components/ui/Switch";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { languages } from "@/i18n/translations";
import type {
  HiddenHomeSectionPreference,
  HintWindowPreference,
  SuggestedTransactionPreference,
} from "@/types";

type UserPreferencesDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

type PreferenceRowProps = {
  checked: boolean;
  disabled?: boolean;
  label: string;
  status: string;
  switchLabel: string;
  onCheckedChange: (checked: boolean) => void;
};

function PreferenceRow({
  checked,
  disabled = false,
  label,
  status,
  switchLabel,
  onCheckedChange,
}: PreferenceRowProps) {
  return (
    <div className="flex min-h-9 items-center gap-2 rounded-lg border border-border/70 bg-card/60 px-3 py-1.5">
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {label}
      </p>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {status}
      </span>
      <Switch
        aria-label={switchLabel}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

export function UserPreferencesDialog({
  isOpen,
  onOpenChange,
}: UserPreferencesDialogProps) {
  const {
    isDark,
    isLoggedIn,
    openCookiePreferences,
    toggleTheme,
    updateUserPreferences,
    userPreferences,
  } = useAppContext();
  const { allEstateList, selectedEstate } = useEstateContext();
  const { language, setLanguage, t } = useLanguage();
  const copy = t.preferences;
  const [preferenceEstateId, setPreferenceEstateId] = useState<number | null>(
    null,
  );
  const estatePreferenceKey =
    preferenceEstateId == null ? null : String(preferenceEstateId);
  const hiddenSuggestedTransactions = estatePreferenceKey
    ? (userPreferences.hideSuggestedBox[estatePreferenceKey] ?? [])
    : [];
  const homeSections: Array<{
    key: HiddenHomeSectionPreference;
    label: string;
  }> = [
    { key: "watchdog", label: copy.watchdog },
    { key: "portfolio", label: copy.portfolio },
  ];
  const suggestedTransactions: Array<{
    key: SuggestedTransactionPreference;
    label: string;
  }> = [
    { key: "rent", label: copy.rent },
    { key: "water", label: copy.water },
    { key: "electricity", label: copy.electricity },
    { key: "advancePayments", label: copy.advancePayments },
    { key: "internet", label: copy.internet },
    { key: "insurance", label: copy.insurance },
  ];
  const hintWindows: Array<{
    key: HintWindowPreference;
    label: string;
  }> = [
    {
      key: "portfolioOverview",
      label: copy.hintPortfolioOverview,
    },
    {
      key: "portfolioPerformance",
      label: copy.hintPortfolioPerformance,
    },
    {
      key: "watchdogOverview",
      label: copy.hintWatchdogOverview,
    },
    {
      key: "watchdogResults",
      label: copy.hintWatchdogResults,
    },
    {
      key: "watchdogPriceDevelopment",
      label: copy.hintWatchdogPriceDevelopment,
    },
    {
      key: "analyticsViewTip",
      label: copy.hintAnalyticsView,
    },
    {
      key: "watchdogFavoritesTip",
      label: copy.hintWatchdogFavorites,
    },
    {
      key: "transactionImportTip",
      label: copy.hintTransactionImport,
    },
    {
      key: "transactionHistoryImportTip",
      label: copy.hintTransactionHistoryImport,
    },
  ];

  useEffect(() => {
    if (!isOpen) return;

    setPreferenceEstateId((current) =>
      current != null && allEstateList.some((estate) => estate.id === current)
        ? current
        : (selectedEstate?.id ?? allEstateList[0]?.id ?? null),
    );
  }, [allEstateList, isOpen, selectedEstate?.id]);

  function setTheme(mode: "light" | "dark") {
    if ((mode === "dark") !== isDark) {
      toggleTheme();
    }
  }

  function showCookiePreferences() {
    onOpenChange(false);
    openCookiePreferences();
  }

  function setHomeSectionHidden(
    section: HiddenHomeSectionPreference,
    hidden: boolean,
  ) {
    const otherSection = section === "watchdog" ? "portfolio" : "watchdog";
    if (hidden && userPreferences.hideSection.includes(otherSection)) return;

    void updateUserPreferences({
      hideSection: hidden
        ? Array.from(new Set([...userPreferences.hideSection, section]))
        : userPreferences.hideSection.filter((item) => item !== section),
    });
  }

  function setSuggestedTransactionHidden(
    transaction: SuggestedTransactionPreference,
    hidden: boolean,
  ) {
    if (!estatePreferenceKey) return;

    const nextHiddenSuggestions = hidden
      ? Array.from(new Set([...hiddenSuggestedTransactions, transaction]))
      : hiddenSuggestedTransactions.filter((item) => item !== transaction);
    const nextPreferences = { ...userPreferences.hideSuggestedBox };

    if (nextHiddenSuggestions.length > 0) {
      nextPreferences[estatePreferenceKey] = nextHiddenSuggestions;
    } else {
      delete nextPreferences[estatePreferenceKey];
    }

    void updateUserPreferences({ hideSuggestedBox: nextPreferences });
  }

  function setHintWindowVisible(
    hint: HintWindowPreference,
    isVisible: boolean,
  ) {
    void updateUserPreferences({
      hintWindows: {
        ...userPreferences.hintWindows,
        [hint]: isVisible,
      },
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-2 p-4 sm:p-5">
        <DialogHeader className="space-y-0.5">
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {copy.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1" aria-labelledby="preferences-mode">
              <h3
                id="preferences-mode"
                className="text-xs font-semibold text-foreground"
              >
                {copy.mode}
              </h3>
              <div className="flex flex-wrap gap-1">
                <Button
                  type="button"
                  variant={!isDark ? "default" : "outline"}
                  className="h-8 min-w-24 justify-start gap-1.5 px-2.5"
                  aria-pressed={!isDark}
                  onClick={() => setTheme("light")}
                >
                  <SunIcon className="size-4" aria-hidden="true" />
                  {copy.lightMode}
                </Button>
                <Button
                  type="button"
                  variant={isDark ? "default" : "outline"}
                  className="h-8 min-w-24 justify-start gap-1.5 px-2.5"
                  aria-pressed={isDark}
                  onClick={() => setTheme("dark")}
                >
                  <MoonIcon className="size-4" aria-hidden="true" />
                  {copy.darkMode}
                </Button>
              </div>
            </div>

            <div
              className="space-y-1"
              aria-labelledby="preferences-language"
            >
              <h3
                id="preferences-language"
                className="text-xs font-semibold text-foreground"
              >
                {copy.language}
              </h3>
              <div className="flex flex-wrap gap-1">
                {languages.map((item) => (
                  <Button
                    key={item.code}
                    type="button"
                    variant={language === item.code ? "default" : "outline"}
                    className="h-8 min-w-24 justify-start gap-1.5 px-2.5"
                    aria-pressed={language === item.code}
                    onClick={() => setLanguage(item.code)}
                  >
                    <span className="text-[11px] font-semibold">
                      {item.shortLabel}
                    </span>
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1" aria-labelledby="preferences-cookies">
            <h3
              id="preferences-cookies"
              className="text-xs font-semibold text-foreground"
            >
              {copy.cookies}
            </h3>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-fit justify-start gap-2 px-2.5"
              onClick={showCookiePreferences}
            >
              <Cog6ToothIcon className="size-4" aria-hidden="true" />
              <span>{copy.manageCookies}</span>
            </Button>
          </div>

          {isLoggedIn ? (
            <div className="space-y-1" aria-labelledby="preferences-hints">
              <h3
                id="preferences-hints"
                className="text-xs font-semibold text-foreground"
              >
                {copy.hints}
              </h3>
              <div className="grid gap-1 sm:grid-cols-2">
                {hintWindows.map((item) => {
                  const isVisible = userPreferences.hintWindows[item.key];

                  return (
                    <PreferenceRow
                      key={item.key}
                      checked={isVisible}
                      label={item.label}
                      status={isVisible ? copy.visible : copy.hidden}
                      switchLabel={`${item.label}: ${
                        isVisible ? copy.visible : copy.hidden
                      }`}
                      onCheckedChange={(checked) =>
                        setHintWindowVisible(item.key, checked)
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {isLoggedIn ? (
            <div
              className="space-y-1"
              aria-labelledby="preferences-homepage"
            >
              <h3
                id="preferences-homepage"
                className="text-xs font-semibold text-foreground"
              >
                {copy.homepage}
              </h3>
              <div className="grid gap-1 sm:grid-cols-2">
                {homeSections.map((item) => {
                  const isHidden = userPreferences.hideSection.includes(
                    item.key,
                  );
                  const otherSection =
                    item.key === "watchdog" ? "portfolio" : "watchdog";
                  const mustRemainVisible =
                    !isHidden &&
                    userPreferences.hideSection.includes(otherSection);

                  return (
                    <PreferenceRow
                      key={item.key}
                      checked={!isHidden}
                      disabled={mustRemainVisible}
                      label={item.label}
                      status={isHidden ? copy.hidden : copy.visible}
                      switchLabel={`${item.label}: ${isHidden ? copy.hidden : copy.visible}`}
                      onCheckedChange={(checked) =>
                        setHomeSectionHidden(item.key, !checked)
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {isLoggedIn && allEstateList.length > 0 ? (
            <div
              className="space-y-1"
              aria-labelledby="preferences-suggestions"
            >
              <div className="flex items-center gap-2">
                <h3
                  id="preferences-suggestions"
                  className="shrink-0 text-xs font-semibold text-foreground"
                >
                  {copy.suggestedTransactions}
                </h3>
                <select
                  aria-label={copy.estate}
                  value={preferenceEstateId ?? ""}
                  onChange={(event) =>
                    setPreferenceEstateId(Number(event.target.value))
                  }
                  className="h-7 min-w-36 max-w-64 rounded-lg border border-border bg-background px-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-ring"
                >
                  {allEstateList.map((estate) => (
                    <option key={estate.id} value={estate.id}>
                      {estate.name}
                    </option>
                  ))}
                </select>
              </div>
              {estatePreferenceKey ? (
                <div className="grid gap-1 sm:grid-cols-2">
                  {suggestedTransactions.map((item) => {
                    const isHidden = hiddenSuggestedTransactions.includes(
                      item.key,
                    );

                    return (
                      <PreferenceRow
                        key={item.key}
                        checked={!isHidden}
                        label={item.label}
                        status={isHidden ? copy.hidden : copy.visible}
                        switchLabel={`${item.label}: ${isHidden ? copy.hidden : copy.visible}`}
                        onCheckedChange={(checked) =>
                          setSuggestedTransactionHidden(item.key, !checked)
                        }
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            size="compact"
            onClick={() => onOpenChange(false)}
          >
            {copy.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
